'use client';

import { AppWindow, BadgeCheck, Grid2x2Plus, MessageCircle } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { Avatar } from '@/components/avatar';
import { TrustflowBadge } from '@/components/discover/agent-discover-card';
import { WidgetMark } from '@/components/workspace/panels/content-preview';
import { appMarkSeed, widgetIconRef } from '@/lib/content/app-identity';
import { coverGradient } from '@/lib/marketplace/cover';
import { trustScoreOf } from '@/lib/reputation/config';
import { cn } from '@/lib/utils';

/**
 * One catalog listing (app or agent) from /api/marketplace/channels, with the
 * A1 join fields. The ONE card the Discover surface renders — the same
 * anatomy as the Apps-panel explore card (cover band, overlapping mark, name,
 * two-line blurb, outlined verb) with the join-powered corrections:
 *
 *  - the mark resolves through `widgetIconRef(widgetMetadata)` (the app's own
 *    declared identity) before falling back to the logo / gradient;
 *  - there is NO `iconEmoji` branch — that column holds `lucide:<name>`
 *    strings for seeded apps and rendering it printed the literal string;
 *  - a maker line ("by <username>"), possible only because the route now
 *    joins the creator.
 *
 * It is also the card of the public `/discover` page (2026-09-30: that
 * page was a text list in a narrow column that looked nothing like
 * Discover). There it runs in LINK MODE (`links`): the same anatomy, but the
 * card, the verb and the maker are real anchors instead of click handlers,
 * because that page exists so a crawler can reach every listing and profile
 * with no JavaScript. The card-wide target is a stretched link on the name
 * (its `::after` covers the card), and the verb and maker sit above it.
 */
export type CatalogListing = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logoUrl: string | null;
  bannerUrl: string | null;
  color: string | null;
  verificationStatus: string | null;
  primaryContentId: string | null;
  primaryAgentId: string | null;
  widgetMetadata: Record<string, unknown> | null;
  widgetPostId: string | null;
  installCount: number | null;
  /** Listing mint date (marketplace publication) — drives the New sort. */
  createdAt?: string | null;
  makerUsername: string | null;
  makerDisplayName: string | null;
  agentUsername: string | null;
  agentDisplayName: string | null;
  agentAvatarUrl: string | null;
  /**
   * The agent's TrustFlow magnitude (`users.scalar_reputation`, a decimal
   * column, so usually a string): the channels route joins it for agent
   * listings, the Mine shelf maps it from `/api/agents`. Absent, null or 0
   * means unscored, and the card shows no badge.
   */
  agentScalarReputation?: number | string | null;
  root: 'apps' | 'agents';
  category: string;
  /** Set on the maker's OWN apps: no marketplace listing behind this one. */
  unpublished?: boolean;
};

/** Link mode's three targets (see the file note). */
export type CatalogCardLinks = {
  /** The listing page, `/c/<slug>`: the whole card. */
  open: string;
  /** The verb (Open / Chat): the spawn deep link. Null draws no verb. */
  use: string | null;
  /** The maker's profile, for the maker line. Null leaves it plain text. */
  maker: string | null;
};

/**
 * The catalog grid at browse density: a 250px card floor (the research sweet
 * spot lands cards at 270-320) and 16px gaps (12 is the visual-merge floor).
 * The floor holds at every width, so a phone gets ONE column of full-width
 * cards (operator call, 2026-09-30): the old 150px phone floor made two
 * columns of cards too narrow for the name, the blurb and the maker line. A
 * wider small screen gets two, and desktop is unchanged. Shared by Discover's
 * overlay and the public /discover page so the two surfaces cannot drift
 * apart.
 */
export const CATALOG_GRID_CLS =
  'grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(250px,1fr))]';

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

/**
 * Public cards show installs only once the number is respectable — below
 * this, the count reads as emptiness (and a seeded fake would read as
 * cooked books). The maker's OWN listing view stays unfloored: their "3
 * installs" is signal. Raise/lower this to tune when counts surface.
 */
const INSTALL_COUNT_FLOOR = 100;

type CatalogCardProps =
  | {
      listing: CatalogListing;
      /** Click anywhere on the card: push the listing's detail level. */
      onOpenListing: (listing: CatalogListing) => void;
      /** The verb button: Open (spawn the app) / Chat (spawn the agent). */
      onUse: (listing: CatalogListing) => void;
      links?: undefined;
    }
  | {
      listing: CatalogListing;
      /** Link mode: anchors instead of handlers (see the file note). */
      links: CatalogCardLinks;
      onOpenListing?: undefined;
      onUse?: undefined;
    };

export function CatalogCard(props: CatalogCardProps) {
  const { listing, links } = props;
  const t = useT();
  const isAgent = !!listing.primaryAgentId;
  const verified = listing.verificationStatus === 'verified';
  // Seeded from the APP, not the channel row — see `appMarkSeed`. The cover
  // band and the mark share the seed, so an app looks like itself here, on
  // its channel page, in the taskbar and in the OG image.
  const markSeed = appMarkSeed(listing.widgetMetadata, listing.id);
  const cover = coverGradient(markSeed, listing.color).css;
  const iconRef = widgetIconRef(listing.widgetMetadata);
  const maker = listing.makerUsername;
  // The same clamped number and badge as Discover's "See all" agent cards
  // (portal visual pass, 2026-09-28: these cards had none). Agents only: an
  // app listing joins no agent, so it has no score of its own.
  const trustflow =
    isAgent && listing.agentScalarReputation != null ? trustScoreOf(listing.agentScalarReputation) : 0;

  const verbClass =
    'inline-flex items-center justify-center gap-1 rounded-md border border-border/60 px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-accent';
  const verbBody = (
    <>
      {isAgent ? <MessageCircle className="h-3 w-3" /> : <Grid2x2Plus className="h-3 w-3" />}
      {isAgent ? t('workspace.canvas.node.chat') : t('posts.open')}
    </>
  );

  return (
    <div
      {...(links
        ? {}
        : {
            role: 'button',
            tabIndex: 0,
            onClick: () => props.onOpenListing?.(listing),
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter') props.onOpenListing?.(listing);
            },
          })}
      // `@container/card`: the TrustFlow badge's word shows only on a card
      // wide enough to keep the name readable beside it (see below).
      className={cn(
        '@container/card group flex flex-col overflow-hidden rounded-xl border border-border/60 bg-background transition-shadow hover:shadow-md',
        // Link mode: `relative` anchors the name's stretched link, and the
        // ring follows focus into any of the card's anchors.
        links
          ? 'relative focus-within:ring-2 focus-within:ring-primary/40'
          : 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40',
      )}
    >
      <div className="relative h-16 w-full" style={{ background: cover }}>
        {/* One badge, top-right of the cover: what KIND of thing this is, or
            that it is not published yet. Sits over the band so the name row
            keeps its two clean read layers. Click-through: in link mode its
            z-10 would otherwise hold a dead patch above the stretched link. */}
        {(isAgent || listing.unpublished) && (
          <span className="pointer-events-none absolute right-2 top-2 z-10 rounded-md bg-background/85 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-foreground/70 shadow-sm backdrop-blur-sm">
            {listing.unpublished ? t('catalogCard.unpublished') : t('user.agent')}
          </span>
        )}
        {listing.bannerUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={listing.bannerUrl} alt="" className="h-full w-full object-cover" />
        )}
        {isAgent ? (
          <Avatar
            seed={listing.agentUsername ?? listing.slug.split('/').pop() ?? listing.id}
            type="agent"
            src={listing.agentAvatarUrl ?? listing.logoUrl}
            className="absolute -bottom-4 left-2.5 h-9 w-9 border-2 border-background bg-white shadow-sm"
          />
        ) : iconRef ? (
          <WidgetMark
            iconRef={iconRef}
            seed={markSeed}
            name={listing.name}
            className="absolute -bottom-4 left-2.5 h-9 w-9 rounded-lg border-2 border-background text-white"
            style={{ background: cover }}
          />
        ) : (
          <div
            className="absolute -bottom-4 left-2.5 flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg border-2 border-background text-white"
            style={{ background: cover }}
          >
            {listing.logoUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={listing.logoUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <AppWindow className="h-4 w-4" strokeWidth={1.6} />
            )}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col px-3 pb-3 pt-5">
        <div className="flex items-center gap-1">
          {links ? (
            <a
              href={links.open}
              className="truncate text-[13px] font-medium after:absolute after:inset-0 focus:outline-none"
            >
              {listing.name}
            </a>
          ) : (
            <span className="truncate text-[13px] font-medium">{listing.name}</span>
          )}
          {verified && (
            <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-sky-500" aria-label={t('workspace.panels.official')} />
          )}
          {/* In the name row, not a row of its own: the overlay's row plan
              is built on a fixed card height (CARD_H). The name truncates
              first, so the badge stays whole. Below 16rem (a phone's
              two-column grid, the inline panel) the word "TrustFlow" is for
              screen readers only and the pill is the shield and the number,
              or it would leave the name a few letters. */}
          <TrustflowBadge
            score={trustflow}
            className="ml-0.5 shrink-0"
            labelClassName="sr-only @3xs/card:not-sr-only"
          />
        </div>
        {/* ONE description line (D2 card diet): the card is for comparison
            scanning — three read layers (name, blurb, verb+maker). The full
            paragraph lives one click away on the listing detail. */}
        {/* An agent's line leads with its handle: the name above is the
            display name, and the handle is what you type to reach it. Same
            line as the blurb rather than a row of its own, because the
            overlay's row plan is built on a fixed card height (CARD_H). */}
        <p className="mt-0.5 line-clamp-1 min-h-[14px] text-[11px] leading-snug text-muted-foreground">
          {isAgent && listing.agentUsername ? (
            <>
              <span data-testid="catalog-card-handle">@{listing.agentUsername}</span>
              {listing.description ? ` · ${listing.description}` : ''}
            </>
          ) : (
            listing.description || ''
          )}
        </p>
        <div className="mt-2 flex items-center justify-between gap-2">
          {links ? (
            links.use && (
              // `relative z-10`: above the name's stretched link. `nofollow`
              // because the spawn lands in /workspace, which robots.txt
              // disallows.
              <a href={links.use} rel="nofollow" className={cn(verbClass, 'relative z-10')}>
                {verbBody}
              </a>
            )
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                props.onUse?.(listing);
              }}
              className={verbClass}
            >
              {verbBody}
            </button>
          )}
          {maker && (
            <span className={cn('truncate text-[10px] text-muted-foreground', links && 'relative z-10')}>
              by{' '}
              {links?.maker ? (
                <a href={links.maker} className="hover:text-foreground hover:underline">
                  @{maker}
                </a>
              ) : (
                `@${maker}`
              )}
              {typeof listing.installCount === 'number' &&
              listing.installCount >= INSTALL_COUNT_FLOOR
                ? ` · ${formatCount(listing.installCount)} installs`
                : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
